import { useMemo, useState } from 'react';
import { Content } from 'src/contexts/ContentContext';
import { PosSpinner } from 'src/core/POS/PosSpinner';
import { vars } from 'src/core/themes';
import { Stack, Switch } from 'src/core/ui';
import { InfoOutlineIcon } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import {
  BulkListingPriceCalculation,
  CompListing,
  Listing,
  ListingPriceCalculation,
} from 'src/WebApiController';

import { ComparableListings } from '../ComparableListings';
import { ListingsCarousel } from '../ListingsCarousel/ListingsCarousel';
import * as styles from './CompListingsPreview.css';
import { usePricingCalculationListings } from './CompListingsPreviewV2.hooks';

export type CompListingsPreviewV2Props = {
  isLoading: boolean;
  showListingsCarousel?: boolean;
  listings: Listing[]; // Seller listings
  selectedListingIds: number[]; // Your selected listing ids
  listingPriceCalculations: BulkListingPriceCalculation[];
  compListings: CompListing[];
  isExcludingFanInventory: boolean;
};

export const CompListingsPreviewV2 = ({
  isLoading,
  showListingsCarousel,
  listings,
  selectedListingIds,
  listingPriceCalculations,
  compListings,
  isExcludingFanInventory,
}: CompListingsPreviewV2Props) => {
  const [selectedListing, setSelectedListing] = useState<Listing | null>(
    listings.length == 1 ? listings[0] : null
  );

  const [showOnlyMyListings, setShowOnlyMyListings] = useState(false);

  const {
    sellerListings,
    comparableListings,
    selectedListingPriceCalculation,
  } = usePricingCalculationListings(
    listings,
    selectedListing,
    selectedListingIds,
    compListings,
    listingPriceCalculations
  );

  const listingPriceCalcWithComps = useMemo(
    (): ListingPriceCalculation | null =>
      selectedListingPriceCalculation
        ? {
            ...selectedListingPriceCalculation,
            compListings,
          }
        : null,
    [compListings, selectedListingPriceCalculation]
  );

  const hasBetterPrice = useMemo(() => {
    const hasBetterPrice = (listingPriceCalc: BulkListingPriceCalculation) => {
      if (
        !listingPriceCalc?.newPrcInclFanInv ||
        !listingPriceCalc?.websitePrice?.amt
      ) {
        return false;
      }
      return (
        listingPriceCalc.newPrcInclFanInv < listingPriceCalc.websitePrice.amt
      );
    };

    if (selectedListing) {
      const listingPriceCalc = listingPriceCalculations.find(
        (lpc) => lpc.listingId === selectedListing.id
      );
      return listingPriceCalc ? hasBetterPrice(listingPriceCalc) : false;
    }

    return listingPriceCalculations.some(hasBetterPrice);
  }, [listingPriceCalculations, selectedListing]);

  return isLoading ? (
    <PosSpinner />
  ) : (
    <Stack direction="column" className={styles.root}>
      {listings.length > 1 && showListingsCarousel && (
        <ListingsCarousel
          listings={listings}
          selectedListing={selectedListing}
          setSelectedListing={setSelectedListing}
        />
      )}

      <Stack
        direction="row"
        gap="m"
        alignItems="center"
        justifyContent="spaceBetween"
      >
        <span>
          <Content id={ContentId.OnlyShowMyListings} />
        </span>
        <Switch
          checked={showOnlyMyListings}
          onChange={(e) => e.stopPropagation()}
          onCheckedChange={(isChecked) => {
            setShowOnlyMyListings(isChecked);
          }}
        />
      </Stack>

      {isExcludingFanInventory && hasBetterPrice && (
        <div className={styles.fanInventoryInfoContainer}>
          <InfoOutlineIcon
            fill={vars.color.textWarning}
            size={vars.iconSize.l}
          />
          <span className={styles.statementText}>
            {selectedListing ? (
              <Content id={ContentId.NonCompetitiveListing} />
            ) : (
              <Content id={ContentId.NonCompetitiveListingBulk} />
            )}
          </span>
        </div>
      )}

      <ComparableListings
        currentListings={sellerListings}
        comparableListings={comparableListings}
        sortBy="price"
        showOnlyMyListings={showOnlyMyListings}
        listingPriceCalc={listingPriceCalcWithComps}
        listingPriceCalculations={listingPriceCalculations}
        floorPrice={selectedListingPriceCalculation?.cmpFlr?.amt}
        ceilingPrice={selectedListingPriceCalculation?.cmpCeil?.amt}
        isExcludingFanInventory={isExcludingFanInventory}
      />
    </Stack>
  );
};
